import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * FAANG-level Button System
 * 
 * Variants:
 * - default (primary): Main CTA, one per screen
 * - secondary: Alternative actions
 * - tertiary: Low-emphasis actions (ghost with subtle styling)
 * - outline: Bordered buttons
 * - ghost: Invisible until hovered
 * - destructive: Dangerous actions
 * - link: Text-like links
 * 
 * Sizes ensure minimum 44px touch target on mobile (Apple HIG)
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-normal sm:whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-all duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.97] min-w-0 max-w-full box-border",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-md hover:shadow-lg hover:scale-105 active:scale-95",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-md hover:shadow-lg hover:scale-105 active:scale-95",
        outline: "border-2 border-input bg-background hover:bg-accent hover:text-accent-foreground hover:scale-105 active:scale-95",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 hover:scale-105 active:scale-95",
        tertiary: "text-muted-foreground hover:text-foreground hover:bg-muted/50",
        ghost: "hover:bg-accent hover:text-accent-foreground hover:scale-105 active:scale-95",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-11 px-5 py-2.5 min-h-[var(--touch-target)]",
        sm: "h-11 rounded-lg px-3 min-h-[var(--touch-target)]",
        lg: "h-12 rounded-lg px-8 min-h-[calc(var(--touch-target)+var(--space-1))] text-base",
        icon: "h-11 w-11 min-h-[var(--touch-target)] min-w-[var(--touch-target)]",
        "icon-sm": "h-11 w-11 min-h-[var(--touch-target)] min-w-[var(--touch-target)]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, children, disabled, ...props }, ref) => {
    // När asChild används kräver Radix Slot exakt ett barn (React.Children.only).
    // Fragment räknas som ett element men innehåller flera barn → Children.only kastar. Exkludera Fragment.
    const arr = React.Children.toArray(children);
    const raw = arr.length === 1 && React.isValidElement(arr[0]) ? arr[0] : null;
    const single = raw?.type === React.Fragment ? null : raw;
    const useSlot = asChild && single !== null;
    const Comp = useSlot ? Slot : "button";
    // When asChild (useSlot), Slot must receive exactly one child (React.Children.only).
    // Do not add Loader2 as a sibling or Slot will throw.
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading}
        {...props}
      >
        {loading && !useSlot && <Loader2 className="h-4 w-4 animate-spin" />}
        {useSlot ? single : children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
